/**
 * Imports
 */
import { CUSTOMER_FILES_STORAGE } from 'config';
import firebase from 'firebase/app';
import { getFirebaseInstance } from './firebaseAdapter';

/**
 * Types
 */
export type StoredFile = {
  name: string;
  /** Path inside the bucket, so a caller can address the object itself later. */
  fullPath: string;
  url: string;
};

/**
 * Storage ref for `path`, in `gsUrl`'s bucket or the project's default bucket.
 */
const refFor = (path: string, gsUrl?: string): firebase.storage.Reference => {
  const storage = getFirebaseInstance().storage();
  return gsUrl ? storage.refFromURL(`${gsUrl}/${path}`) : storage.ref(path);
};

/**
 * Buckets to try for a read or delete, most likely first — the same order and for the
 * same reason as the new support dashboard's `bucketCandidates` (`lib/storage.ts`).
 *
 * Onboarding documents and owner ID files are being consolidated into customer-files,
 * but objects written before that are still in the project's default bucket, so both
 * have to be tried. An explicitly recorded bucket (`owner.bucketName` and friends) is a
 * record of where a file actually went, so it wins when present.
 *
 * ⚠️ Trying **customer-files even when nothing records it** is the point: the bucket
 * fields are only written by the new dashboard, so for everything older the file can
 * sit in customer-files while no field says so. Probing is also what makes path
 * guesses such as `path.includes('acct_')` unnecessary — those were a crude stand-in
 * for the same probe, and they get it wrong whenever a merchant's *new* files were
 * written under its *old* path.
 *
 * Uploads deliberately do not use this — see {@link uploadToPath}.
 */
const bucketCandidates = (recordedBucket?: string): (string | undefined)[] => {
  const candidates: (string | undefined)[] = [];
  if (recordedBucket) {
    candidates.push(`gs://${recordedBucket}`);
  }
  if (CUSTOMER_FILES_STORAGE) {
    candidates.push(CUSTOMER_FILES_STORAGE);
  }
  // Project default — where pre-migration objects live.
  candidates.push(undefined);
  return candidates.filter((c, i) => candidates.indexOf(c) === i);
};

const describe = (gsUrl?: string) => gsUrl ?? '(project default)';

/**
 * Resolve a stored path to a download URL by trying each candidate bucket in turn,
 * logging every attempt so it is visible which bucket actually served the file. Throws
 * the last failure when no bucket has the object, so "missing" stays distinguishable
 * from "no permission" (the error `code` is logged either way).
 */
const resolveDownloadUrl = async (
  path: string,
  recordedBucket?: string,
): Promise<string> => {
  const candidates = bucketCandidates(recordedBucket);
  console.log('[storage] resolving', path, {
    recordedBucket: recordedBucket ?? '(none recorded)',
    customerFilesBucket: CUSTOMER_FILES_STORAGE,
    willTry: candidates.map(describe),
  });

  let lastError: any;
  for (const gsUrl of candidates) {
    const fileRef = refFor(path, gsUrl);
    try {
      const url = await fileRef.getDownloadURL();
      console.log(
        `[storage] ✓ FOUND in gs://${fileRef.bucket}/${fileRef.fullPath}`,
      );
      return url;
    } catch (error) {
      console.warn(
        `[storage] ✗ not in gs://${fileRef.bucket} —`,
        (error && (error as any).code) || error,
      );
      lastError = error;
    }
  }
  throw lastError;
};

/**
 * Kept for callers that only ever read the project's default bucket historically —
 * it now probes like everything else, so a migrated file still resolves.
 */
const getDownloadUrl = (path: string): Promise<string> =>
  resolveDownloadUrl(path);

const listOneBucket = async (
  path: string,
  gsUrl?: string,
): Promise<StoredFile[]> => {
  const result = await refFor(path, gsUrl).listAll();
  return Promise.all(
    result.items.map(async (item) => ({
      name: item.name,
      fullPath: item.fullPath,
      url: await item.getDownloadURL(),
    })),
  );
};

/**
 * List the files directly under `path`, merging customer-files with the default bucket.
 *
 * A merchant onboarded before the migration can have older documents in the default
 * bucket and newer ones in customer-files under the *same* path — onboarding writes to
 * the default bucket and records `companyDocumentsPath`, and the new dashboard then
 * uploads to that same recorded path in customer-files — so listing only one bucket
 * hides half the folder. Customer-files wins a path collision.
 *
 * One bucket failing is expected mid-migration (folder absent, or no access) and
 * contributes nothing. Every bucket failing is not, and rethrows rather than
 * presenting an empty folder as the truth.
 */
const listFolder = async (path: string): Promise<StoredFile[]> => {
  const candidates = bucketCandidates();
  const settled = await Promise.all(
    candidates.map(async (gsUrl) => {
      try {
        const files = await listOneBucket(path, gsUrl);
        console.log(
          `[storage] listed ${files.length} file(s) in ${describe(
            gsUrl,
          )}/${path}`,
        );
        return { files } as { files?: StoredFile[]; error?: any };
      } catch (error) {
        console.warn(
          `[storage] ✗ could not list ${describe(gsUrl)}/${path} —`,
          (error && (error as any).code) || error,
        );
        return { error } as { files?: StoredFile[]; error?: any };
      }
    }),
  );

  const fulfilled = settled.filter((r) => r.files !== undefined);
  if (fulfilled.length === 0) {
    throw settled[0].error;
  }
  // Candidate order is preserved, so customer-files is seen before the default bucket.
  const byPath = new Map<string, StoredFile>();
  fulfilled.forEach(({ files }) =>
    files!.forEach((file) => {
      if (!byPath.has(file.fullPath)) {
        byPath.set(file.fullPath, file);
      }
    }),
  );
  return Array.from(byPath.values());
};

/**
 * Upload a file to `path` in the customer-files bucket.
 *
 * Uploads are never routed to the default bucket, and never to the bucket a merchant's
 * older files happen to sit in: everything new belongs in customer-files. Reads merge
 * both buckets, so a folder split across the two still lists whole.
 *
 * Returns the bare bucket name actually written to — read back off the ref rather than
 * re-derived from the `gs://` URL — so callers can record it on the file's own record.
 */
const uploadToPath = async (
  path: string,
  file: File,
  metadata?: firebase.storage.UploadMetadata,
): Promise<string> => {
  // Guard rather than quietly falling back: an unset bucket would route KYC files to
  // the project default, the exact misplacement this module exists to prevent.
  if (!CUSTOMER_FILES_STORAGE) {
    throw new Error(
      'CUSTOMER_FILES_STORAGE is not configured — refusing to upload',
    );
  }
  const uploadRef = refFor(path, CUSTOMER_FILES_STORAGE);
  console.log(
    `[storage] uploading to gs://${uploadRef.bucket}/${uploadRef.fullPath}`,
  );
  await uploadRef.put(file, metadata);
  return uploadRef.bucket;
};

/**
 * Delete the object at `path` from whichever candidate bucket actually holds it, so a
 * file that displays is a file that can be removed. Throws if no bucket had it, so a
 * failed purge is never counted as a success.
 */
const deleteAtPath = async (
  path: string,
  recordedBucket?: string,
): Promise<void> => {
  let lastError: any;
  for (const gsUrl of bucketCandidates(recordedBucket)) {
    const fileRef = refFor(path, gsUrl);
    try {
      await fileRef.delete();
      console.log(
        `[storage] ✓ DELETED gs://${fileRef.bucket}/${fileRef.fullPath}`,
      );
      return;
    } catch (error) {
      console.warn(
        `[storage] ✗ could not delete from gs://${fileRef.bucket} —`,
        (error && (error as any).code) || error,
      );
      lastError = error;
    }
  }
  throw lastError;
};

export type PurgeResult = {
  /** Objects deleted. */
  deleted: number;
  /** Objects that would not delete — that data is still in the bucket. */
  failed: number;
  /** Buckets that would not even list, so their contents are unaccounted for. */
  unreadableBuckets: number;
};

/**
 * Delete everything directly under `path` in **every** candidate bucket.
 *
 * Used by the GDPR purges, which is why it counts rather than swallows: personal data
 * left behind in a bucket is not something to hide, and a purge that only emptied the
 * default bucket would leave the customer-files copies in place.
 */
const deleteFolder = async (path: string): Promise<PurgeResult> => {
  let deleted = 0;
  let failed = 0;
  let unreadableBuckets = 0;
  for (const gsUrl of bucketCandidates()) {
    let items: firebase.storage.Reference[];
    try {
      items = (await refFor(path, gsUrl).listAll()).items;
    } catch (error) {
      // A folder that never existed in a bucket lists as empty, so this is a real
      // failure — access, or a bucket this environment does not have — and it hides
      // files we cannot even count. Kept separate from `failed`, which is objects.
      console.warn(
        `[storage] ✗ could not list ${describe(gsUrl)}/${path} for deletion —`,
        (error && (error as any).code) || error,
      );
      unreadableBuckets += 1;
      continue;
    }
    const results = await Promise.all(
      items.map(async (item) => {
        try {
          await item.delete();
          return true;
        } catch (error) {
          console.warn(
            `[storage] ✗ could not delete gs://${item.bucket}/${item.fullPath} —`,
            (error && (error as any).code) || error,
          );
          return false;
        }
      }),
    );
    deleted += results.filter(Boolean).length;
    failed += results.filter((ok) => !ok).length;
  }
  console.log(`[storage] purged ${path}:`, {
    deleted,
    failed,
    unreadableBuckets,
  });
  return { deleted, failed, unreadableBuckets };
};

/**
 * Exports
 */
export {
  bucketCandidates,
  deleteAtPath,
  deleteFolder,
  getDownloadUrl,
  listFolder,
  refFor,
  resolveDownloadUrl,
  uploadToPath,
};
